import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase, getUserProfile, getUserProfileByEmail, signOut } from '../lib/supabase';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [authMode, setAuthMode] = useState(null); // 'supabase' or 'demo'

  // Initialize auth state from Supabase session
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check for existing Supabase session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          setUser(session.user);
          setToken(session.access_token);
          setAuthMode('supabase');
          
          // Fetch user profile from users table
          const profile = await getUserProfile(session.user.id);
          if (profile) {
            setUserProfile(profile);
          }
        } else {
          // Check for demo mode session in localStorage
          const demoUser = localStorage.getItem('demo_user');
          if (demoUser) {
            setUser(JSON.parse(demoUser));
            setAuthMode('demo');
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        setUser(session.user);
        setToken(session.access_token);
        setAuthMode('supabase');
        
        // Fetch user profile
        const profile = await getUserProfile(session.user.id);
        if (profile) {
          setUserProfile(profile);
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setUserProfile(null);
        setToken(null);
        setAuthMode(null);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Supabase login with email and password
  const login = async (email, password) => {
    try {
      // Add timeout wrapper to prevent hanging forever
      const loginPromise = supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Login timeout - please try again')), 15000)
      );
      
      // Race between login and timeout
      const { data, error } = await Promise.race([loginPromise, timeoutPromise]);
      
      if (error) {
        // Provide user-friendly messages for common errors
        let errorMessage = error.message || 'Login failed';
        
        // Handle specific Supabase error messages
        if (errorMessage.includes('Email not confirmed')) {
          errorMessage = 'Please confirm your email address. Check your inbox for a confirmation link.';
        } else if (errorMessage.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please check your credentials.';
        } else if (errorMessage.includes('User not found')) {
          errorMessage = 'No account found with this email. Please sign up first.';
        } else if (errorMessage.includes('body stream already read') || 
                   errorMessage.includes('Body is disturbed') || 
                   errorMessage.includes('Body is locked')) {
          // These errors mask the real error - treat as invalid credentials
          errorMessage = 'Invalid email or password. Please check your credentials.';
        }
        
        console.log('Supabase login error:', error.message);
        return { success: false, error: errorMessage };
      }
      
      if (data?.user && data?.session) {
        // Set auth state immediately so user doesn't see loading forever
        setUser(data.user);
        setToken(data.session.access_token);
        setAuthMode('supabase');
        
        // Store token in localStorage for API calls
        localStorage.setItem('token', data.session.access_token);
        localStorage.setItem('supabase_token', data.session.access_token);
        
        // Create a minimal profile immediately - don't wait for DB fetch
        const minimalProfile = {
          id: data.user.id,
          email: data.user.email,
          full_name: data.user.user_metadata?.full_name || data.user.email?.split('@')[0] || 'Admin User',
          role: 'CEO',
          primary_lane: 'Strategy & Architecture',
          level: 'L4',
          department: 'Executive',
          region: 'Global'
        };
        setUserProfile(minimalProfile);
        
        // Try to fetch profile from DB in background (non-blocking)
        fetchProfileInBackground(data.user.email, data.user.id);
        
        return { success: true };
      }
      
      return { success: false, error: 'Login failed. Please try again.' };
    } catch (err) {
      console.error('Login error:', err);
      // Handle timeout and various body stream errors
      const errMsg = err.message || '';
      if (errMsg.includes('timeout')) {
        return { 
          success: false, 
          error: 'Login timed out. Please check your internet connection and try again.'
        };
      }
      if (errMsg.includes('body stream already read') || 
          errMsg.includes('Body is disturbed') || 
          errMsg.includes('Body is locked')) {
        return { 
          success: false, 
          error: 'Invalid email or password. Please check your credentials.'
        };
      }
      return { 
        success: false, 
        error: err.message || 'Login failed. Please try again.'
      };
    }
  };
  
  // Fetch profile in background without blocking login
  const fetchProfileInBackground = async (email, userId) => {
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
      );
      
      const fetchPromise = (async () => {
        let profile = await getUserProfileByEmail(email);
        if (!profile) {
          profile = await getUserProfile(userId);
        }
        return profile;
      })();
      
      const profile = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (profile) {
        setUserProfile(profile);
      }
    } catch (err) {
      console.log('Background profile fetch failed (using default):', err.message);
      // Keep the minimal profile we already set
    }
  };

  // Register with Supabase (for future use)
  const register = async (name, email, password) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: name }
        }
      });
      
      if (error) throw error;
      
      if (data.user) {
        return { success: true, needsConfirmation: !data.session };
      }
      
      return { success: false, error: 'Registration failed' };
    } catch (error) {
      console.error('Register error:', error);
      return { 
        success: false, 
        error: error.message || 'Registration failed' 
      };
    }
  };

  // Demo login - still works alongside Supabase
  const demoLogin = async () => {
    try {
      const response = await authAPI.demoLogin();
      const { token: demoToken, user: demoUser } = response.data;
      
      localStorage.setItem('demo_user', JSON.stringify(demoUser));
      localStorage.setItem('demo_token', demoToken);
      localStorage.setItem('token', demoToken); // Also save as 'token' for API interceptor
      
      setToken(demoToken);
      setUser(demoUser);
      setAuthMode('demo');
      
      // Set a mock profile for demo mode
      setUserProfile({
        id: demoUser.id,
        email: demoUser.email,
        full_name: demoUser.name || 'Demo User',
        role: 'CEO',
        primary_lane: 'Vision & Expansion',
        level: 'L4',
        department: 'Executive',
        region: 'Global',
        avatar_url: null
      });
      
      return { success: true };
    } catch (error) {
      console.error('Demo login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Demo login failed' 
      };
    }
  };

  // Logout - handles both Supabase and demo mode
  const logout = async () => {
    try {
      if (authMode === 'supabase') {
        await signOut();
      }
      
      // Clear all auth data
      localStorage.removeItem('demo_user');
      localStorage.removeItem('demo_token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      setUser(null);
      setUserProfile(null);
      setToken(null);
      setAuthMode(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Get the effective user role for view determination
  const getUserRole = () => {
    if (userProfile?.role) {
      return userProfile.role;
    }
    return 'Team Member'; // Default role
  };

  // Check if user has a specific role
  const hasRole = (role) => {
    return getUserRole() === role;
  };

  // Determine which view the user should see based on role
  const getViewPermissions = () => {
    const role = getUserRole();
    const region = userProfile?.region;
    const department = userProfile?.department;
    const primaryLane = userProfile?.primary_lane;
    const level = userProfile?.level;
    
    switch (role) {
      case 'CEO':
        return {
          canViewFullPlatform: true,
          canViewAllRegions: true,
          canViewAllTeams: true,
          canViewAllDepartments: true,
          canAccessExecutiveFeatures: true,
          canAccessCommandSuite: true,
          canViewBurnoutAlerts: true,
          canViewExecutiveReviews: true,
          canViewInsights: true,
          canViewStructure: true,
          dashboardView: 'executive',
          navigationItems: 'all'
        };
      case 'Chief of Staff':
        return {
          canViewFullPlatform: true,
          canViewAllRegions: true,
          canViewAllTeams: true,
          canViewAllDepartments: true,
          canAccessExecutiveFeatures: true,
          canAccessCommandSuite: true,
          canViewBurnoutAlerts: true,
          canViewExecutiveReviews: true,
          canViewInsights: true,
          canViewStructure: true,
          dashboardView: 'coordination',
          navigationItems: 'all'
        };
      case 'Regional Director':
        return {
          canViewFullPlatform: false,
          canViewAllRegions: false,
          canViewAllTeams: false,
          canViewAllDepartments: false,
          canAccessExecutiveFeatures: false,
          canAccessCommandSuite: false,
          canViewBurnoutAlerts: true,
          canViewExecutiveReviews: false,
          canViewInsights: true,
          canViewStructure: true,
          dashboardView: 'regional',
          regionFilter: region,
          navigationItems: 'regional'
        };
      case 'Team Member':
      default:
        return {
          canViewFullPlatform: false,
          canViewAllRegions: false,
          canViewAllTeams: false,
          canViewAllDepartments: false,
          canAccessExecutiveFeatures: false,
          canAccessCommandSuite: false,
          canViewBurnoutAlerts: false,
          canViewExecutiveReviews: false,
          canViewInsights: false,
          canViewStructure: false,
          dashboardView: 'personal',
          regionFilter: region,
          departmentFilter: department,
          laneFilter: primaryLane,
          navigationItems: 'personal'
        };
    }
  };

  // Get role display info for UI
  const getRoleDisplayInfo = () => {
    const role = getUserRole();
    
    const roleConfigs = {
      'CEO': {
        color: '#00C9A7',
        label: 'Executive',
        description: 'Full platform access'
      },
      'Chief of Staff': {
        color: '#8B5CF6',
        label: 'Coordinator',
        description: 'Organization-wide coordination'
      },
      'Regional Director': {
        color: '#3B82F6',
        label: 'Regional',
        description: `${userProfile?.region || 'Regional'} oversight`
      },
      'Team Member': {
        color: '#6B7280',
        label: 'Personal',
        description: 'Personal workspace'
      }
    };
    
    return roleConfigs[role] || roleConfigs['Team Member'];
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      userProfile,
      token, 
      loading, 
      authMode,
      login, 
      register, 
      demoLogin, 
      logout,
      getUserRole,
      hasRole,
      getViewPermissions,
      getRoleDisplayInfo
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
